import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchUsers, sendPayment, getHistory } from '../utils/api';
import { usePlatform } from '../context/PlatformContext';

function normalizeIdentifier(val) {
  if (!val) return val;
  const trimmed = val.trim();
  if (/^[\d\s\(\)\-\+\.]+$/.test(trimmed)) return trimmed.replace(/[^\d+]/g, '');
  return trimmed.toLowerCase();
}

export default function SendPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Send';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate  = useNavigate();
  const platform  = usePlatform();
  const searchRef = useRef(null);

  const [step, setStep]           = useState(1);
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [amount, setAmount]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);

  const amountCents = Math.round(parseFloat(amount||0)*100);
  const feeCents    = Math.round(amountCents * (platform.feeBps / 10000));
  const netCents    = amountCents - feeCents;

  useEffect(() => { loadRecentContacts(); }, []);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchUsers(query);
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  async function loadRecentContacts() {
    try {
      const res = await getHistory();
      const sends = res.data.filter(tx => tx.type === 'send' && tx.counterparty);
      const seen = new Set();
      const unique = sends.filter(tx => {
        if (seen.has(tx.counterparty)) return false;
        seen.add(tx.counterparty);
        return true;
      }).slice(0, 5);
      setRecentContacts(unique);
    } catch (err) { console.error(err); }
  }

  function selectRecipient(user) {
    const id = normalizeIdentifier(user.email || user.phone || '');
    setRecipient(user);
    setIdentifier(id);
    setQuery('');
    setResults([]);
    setError('');
    setStep(2);
  }

  function selectRecentContact(contact) {
    const id = normalizeIdentifier(contact.counterpartyEmail || contact.counterpartyPhone || '');
    if (!id) { setError('Cannot find contact details. Search manually.'); return; }
    setRecipient({ name: contact.counterparty, email: contact.counterpartyEmail, phone: contact.counterpartyPhone });
    setIdentifier(id);
    setQuery('');
    setResults([]);
    setError('');
    setStep(2);
  }

  async function handleSend() {
    if (amountCents < platform.minSendCents) {
      setError(platform.minSendCents > 0 ? `Minimum is $${(platform.minSendCents/100).toFixed(2)}` : 'Please enter an amount');
      return;
    }
    setError(''); setLoading(true);
    try {
      await sendPayment(identifier, amountCents);
      setSuccess({ recipient, netCents });
    } catch (err) {
      setError(err.response?.data?.error || 'Send failed');
    } finally { setLoading(false); }
  }

  function getInitials(name) {
    return (name || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2);
  }

  if (success) return (
    <div style={s.page}>
      <div style={s.successWrap}>
        <div style={s.successIcon}>✓</div>
        <div style={s.successTitle}>${(success.netCents/100).toFixed(2)} sent</div>
        <div style={s.successTo}>to {success.recipient.name}</div>
        <button style={s.btn} onClick={() => navigate('/dashboard')}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => step===1 ? navigate('/dashboard') : (setStep(1), setRecipient(null), setAmount(''), setError(''))}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Send Money</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        {step === 1 ? (
          <>
            <div style={s.stepHint}>Step 1 of 2 — Find recipient</div>

            {/* Search box */}
            <div style={s.card}>
              <label style={s.fieldLabel}>Search by name, email, or phone</label>
              <div style={s.searchRow}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="2" style={{flexShrink:0}}>
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  ref={searchRef}
                  style={s.searchInput}
                  type="text"
                  placeholder="Type a name, email, or phone..."
                  value={query}
                  autoFocus
                  onChange={e => { setQuery(e.target.value); setError(''); }}
                />
                {query.length > 0 && (
                  <button onClick={() => { setQuery(''); setResults([]); }} style={s.clearBtn}>✕</button>
                )}
              </div>
            </div>

            {/* Search results */}
            {query.length >= 2 && (
              <div style={s.resultsList}>
                {searching ? (
                  <div style={s.searchingMsg}>Searching...</div>
                ) : results.length === 0 ? (
                  <div style={s.noResults}>No users found for "{query}"</div>
                ) : (
                  results.map(user => (
                    <button key={user.id} style={s.resultRow} onClick={() => selectRecipient(user)}>
                      <div style={s.avatar}>{getInitials(user.name)}</div>
                      <div style={s.resultInfo}>
                        <div style={s.resultName}>{user.name}</div>
                        <div style={s.resultSub}>
                          {[user.email, user.phone].filter(Boolean).join(' · ') || 'No contact info'}
                        </div>
                      </div>
                      <div style={s.resultArrow}>→</div>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Recent contacts */}
            {query.length < 2 && recentContacts.length > 0 && (
              <div style={s.recentWrap}>
                <div style={s.recentTitle}>Recent</div>
                {recentContacts.map((contact, i) => (
                  <button key={i} style={s.resultRow} onClick={() => selectRecentContact(contact)}>
                    <div style={s.avatar}>{getInitials(contact.counterparty)}</div>
                    <div style={s.resultInfo}>
                      <div style={s.resultName}>{contact.counterparty}</div>
                      <div style={s.resultSub}>
                        {[contact.counterpartyEmail, contact.counterpartyPhone].filter(Boolean).join(' · ') || 'No contact info'}
                      </div>
                    </div>
                    <div style={s.resultArrow}>→</div>
                  </button>
                ))}
              </div>
            )}

            {error && <div style={s.error}>{error}</div>}
          </>
        ) : (
          <>
            <div style={s.stepHint}>Step 2 of 2 — Enter amount</div>

            <div style={s.recipientCard}>
              <div style={s.recipientAvatar}>{getInitials(recipient.name)}</div>
              <div style={s.recipientInfo}>
                <div style={s.recipientName}>{recipient.name}</div>
                <div style={s.recipientSub}>
                  {[recipient.email, recipient.phone].filter(Boolean).join(' · ') || identifier}
                </div>
              </div>
              <div style={s.recipientCheck}>✓</div>
            </div>

            <div style={s.amountCard}>
              <label style={s.fieldLabel}>Amount</label>
              <div style={s.amountRow}>
                <span style={s.dollar}>$</span>
                <input
                  style={s.amountInput}
                  type="number"
                  placeholder="0.00"
                  min="1"
                  step="0.01"
                  autoFocus
                  value={amount}
                  onChange={e => { setAmount(e.target.value); setError(''); }}
                />
              </div>
            </div>

            <div style={s.quickRow}>
              {[5,10,20,50].map(v => (
                <button key={v}
                  style={{...s.quickBtn, ...(Number(amount)===v ? s.quickBtnActive : {})}}
                  onClick={() => setAmount(String(v))}>
                  ${v}
                </button>
              ))}
            </div>

            <div style={s.summaryCard}>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>{`Service fee (${platform.feePct}%)`}</span>
                <span style={s.summaryValue}>${(feeCents/100).toFixed(2)}</span>
              </div>
              <div style={s.divider}/>
              <div style={s.summaryRow}>
                <span style={{...s.summaryLabel, fontWeight:600, color:'#1D1D1F'}}>{recipient.name} receives</span>
                <span style={{...s.summaryValue, fontWeight:600, color:'#1D1D1F'}}>${(netCents/100).toFixed(2)}</span>
              </div>
            </div>

            {error && <div style={s.error}>{error}</div>}

            <button style={s.btn} onClick={handleSend} disabled={loading}>
              {loading ? 'Sending...' : `Send $${(amountCents/100).toFixed(2)}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:           { minHeight:'100vh', background:'#F5F5F7', paddingBottom:88 },
  header:         { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px' },
  back:           { width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)' },
  headerTitle:    { fontSize:17, fontWeight:600, color:'#1D1D1F' },
  body:           { padding:'0 16px' },
  stepHint:       { fontSize:12, fontWeight:500, color:'#AEAEB2', marginBottom:12, textAlign:'center' },
  card:           { background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  fieldLabel:     { fontSize:12, fontWeight:500, color:'#6E6E73', display:'block', marginBottom:8 },
  searchRow:      { display:'flex', alignItems:'center', gap:10, background:'#FAFAFA', border:'1.5px solid #F0F0F0', borderRadius:12, padding:'12px 14px' },
  searchInput:    { flex:1, border:'none', background:'transparent', fontSize:15, color:'#1D1D1F', outline:'none' },
  clearBtn:       { background:'none', border:'none', color:'#AEAEB2', fontSize:14, cursor:'pointer', padding:0, lineHeight:1 },
  resultsList:    { background:'#fff', borderRadius:20, overflow:'hidden', marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  searchingMsg:   { padding:'16px 20px', fontSize:14, color:'#AEAEB2', textAlign:'center' },
  noResults:      { padding:'16px 20px', fontSize:14, color:'#AEAEB2', textAlign:'center' },
  recentWrap:     { background:'#fff', borderRadius:20, overflow:'hidden', marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  recentTitle:    { fontSize:12, fontWeight:600, color:'#AEAEB2', padding:'14px 20px 6px', textTransform:'uppercase', letterSpacing:0.5 },
  resultRow:      { display:'flex', alignItems:'center', gap:12, padding:'12px 20px', background:'none', border:'none', borderBottom:'1px solid #F5F5F7', width:'100%', cursor:'pointer', textAlign:'left' },
  avatar:         { width:40, height:40, borderRadius:'50%', background:'#EBF4FF', color:'#007AFF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600, flexShrink:0 },
  resultInfo:     { flex:1 },
  resultName:     { fontSize:14, fontWeight:600, color:'#1D1D1F' },
  resultSub:      { fontSize:12, color:'#AEAEB2', marginTop:2 },
  resultArrow:    { fontSize:14, color:'#AEAEB2' },
  recipientCard:  { background:'#fff', borderRadius:20, padding:16, marginBottom:12, display:'flex', alignItems:'center', gap:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  recipientAvatar:{ width:44, height:44, borderRadius:'50%', background:'#EBF4FF', color:'#007AFF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:600, flexShrink:0 },
  recipientInfo:  { flex:1 },
  recipientName:  { fontSize:15, fontWeight:600, color:'#1D1D1F' },
  recipientSub:   { fontSize:12, color:'#6E6E73', marginTop:2 },
  recipientCheck: { width:24, height:24, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:600 },
  amountCard:     { background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  amountRow:      { display:'flex', alignItems:'center', gap:4 },
  dollar:         { fontSize:36, fontWeight:600, color:'#1D1D1F' },
  amountInput:    { fontSize:36, fontWeight:600, border:'none', background:'transparent', width:'100%', outline:'none', color:'#1D1D1F' },
  quickRow:       { display:'flex', gap:8, marginBottom:12 },
  quickBtn:       { flex:1, padding:'10px 0', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:14, fontWeight:500, color:'#1D1D1F' },
  quickBtnActive: { borderColor:'#007AFF', color:'#007AFF', background:'#EBF4FF' },
  summaryCard:    { background:'#fff', borderRadius:16, padding:16, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  summaryRow:     { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0' },
  summaryLabel:   { fontSize:14, color:'#6E6E73' },
  summaryValue:   { fontSize:14, color:'#6E6E73' },
  divider:        { height:1, background:'#F5F5F7', margin:'10px 0' },
  error:          { background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12 },
  btn:            { width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3 },
  successWrap:    { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:24, gap:12, textAlign:'center' },
  successIcon:    { width:64, height:64, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28 },
  successTitle:   { fontSize:28, fontWeight:700, letterSpacing:-1 },
  successTo:      { fontSize:15, color:'#6E6E73' },
};
